import { useMutation, useQueryClient } from '@tanstack/react-query';
import rentalExtensionService from '../../services/rentalExtensionService';
import { showAlert } from '../../utils/alert';
import { queryKeys } from '../../queryClient';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';

// Create extension
export const useCreateRentalExtension = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      rentalId, 
      data, 
      autoApprove = false 
    }: { 
      rentalId: number; 
      data: {
        extension_days: number;
        new_rental_fee?: number;
        reason?: string;
        notes?: string;
      };
      autoApprove?: boolean;
    }) => {
      return rentalExtensionService.createExtension(rentalId, data, autoApprove);
    },
    onSuccess: (data, variables) => {
      // BUG-S122-EXTSTATE FIX: modal ส่ง auto_approve=true เสมอ แต่ backend จะ "ลดระดับ" เป็น
      // คำขอรออนุมัติเงียบๆ เมื่อผู้เรียกไม่มีสิทธิ์ rentals.approve (rental_extensions.py:68-72)
      // → เดิม toast บอก "ต่อการเช่าสำเร็จ" ทุกกรณี ทำให้ Sales เข้าใจว่าต่อสัญญาแล้ว
      // ทั้งที่ยังรออนุมัติ และวันสิ้นสุดยังไม่ขยับ. ตอนนี้อ่าน is_approved จาก response จริง
      if (data?.is_approved === true) {
        showAlert('สำเร็จ', 'ต่อการเช่าสำเร็จ', 'success');
      } else {
        showAlert(
          'ส่งคำขอแล้ว',
          'ส่งคำขอต่อการเช่าแล้ว รอผู้มีสิทธิ์อนุมัติ — วันสิ้นสุดการเช่าจะยังไม่เปลี่ยนจนกว่าคำขอจะได้รับอนุมัติ',
          'info'
        );
      }
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.rentals.detail(variables.rentalId) });
      queryClient.invalidateQueries({ queryKey: ['rental-extensions', variables.rentalId] });
    },
    onError: (error: any) => {
      const message = getApiErrorMessage(error, 'เกิดข้อผิดพลาดในการต่อการเช่า');
      showAlert('เกิดข้อผิดพลาด', message, 'error');
    }
  });
};

// Approve extension
export const useApproveRentalExtension = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (extensionId: number) => {
      return rentalExtensionService.approveExtension(extensionId);
    },
    onSuccess: () => {
      showAlert('สำเร็จ', 'อนุมัติการต่อเช่าสำเร็จ', 'success');
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all });
      queryClient.invalidateQueries({ queryKey: ['rental-extensions'] });
      queryClient.invalidateQueries({ queryKey: ['pending-extensions'] });
    },
    onError: (error: any) => {
      const message = getApiErrorMessage(error, 'เกิดข้อผิดพลาดในการอนุมัติ');
      showAlert('เกิดข้อผิดพลาด', message, 'error');
    }
  });
};